import type { Room } from 'matrix-js-sdk/src/matrix';

import { getMatrixDisplayName } from './matrixDisplayName';
import type { MentionData } from '../components/Messenger/MentionInput/mentionInputUtils';

/** Ordered mention list from Matrix HTML body (our client and Element-style links). */
export function parseMentionsFromFormattedBody(html: string | undefined): MentionData[] {
  if (!html) return [];
  const re = /<a href="https:\/\/matrix\.to\/#\/([^"]+)">@([^<]*)<\/a>/gi;
  const out: MentionData[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    let id = m[1];
    try {
      id = decodeURIComponent(m[1]);
    } catch {
      /* keep raw */
    }
    out.push({ id, display: m[2] });
  }
  return out;
}

/**
 * Match `m.mentions.user_ids` to @display spans in the plain body in list order
 * (same order our client emits).
 */
export function mentionDataFromBodyAndUserIds(body: string, userIds: string[], room: Room | null): MentionData[] {
  if (!userIds.length || !room) return [];
  const result: MentionData[] = [];
  let searchFrom = 0;
  for (const userId of userIds) {
    const member = room.getMember(userId);
    const display = getMatrixDisplayName(member?.rawDisplayName, userId);
    const needle = `@${display}`;
    const idx = body.indexOf(needle, searchFrom);
    if (idx === -1) return [];
    result.push({ id: userId, display });
    searchFrom = idx + needle.length;
  }
  return result;
}

/**
 * Extract the set of mentioned user IDs from an event content object.
 * Checks `m.mentions.user_ids` first, then falls back to parsing matrix.to
 * anchor hrefs from `formatted_body`.
 */
export function getMentionUserIdsFromContent(content: Record<string, unknown> | undefined): string[] {
  if (!content) return [];
  const mentions = content['m.mentions'] as { user_ids?: string[] } | undefined;
  if (mentions?.user_ids?.length) return mentions.user_ids;
  const formatted = content.formatted_body;
  if (typeof formatted === 'string') {
    return parseMentionsFromFormattedBody(formatted).map((m) => m.id);
  }
  return [];
}

/**
 * Returns the user IDs that are in `next` but not in `prev` (newly added mentions).
 * Both arrays may contain duplicates; the comparison is multiset-aware.
 */
export function newMentionUserIds(prev: string[], next: string[]): string[] {
  const prevCounts = new Map<string, number>();
  for (const id of prev) prevCounts.set(id, (prevCounts.get(id) ?? 0) + 1);
  const result: string[] = [];
  for (const id of next) {
    const remaining = prevCounts.get(id) ?? 0;
    if (remaining > 0) {
      prevCounts.set(id, remaining - 1);
    } else {
      result.push(id);
    }
  }
  return result;
}

export function resolveMentionDataForEdit(
  body: string,
  content: Record<string, unknown> | undefined,
  room: Room | null
): MentionData[] {
  const rawIds = content?.['m.mentions'] as { user_ids?: string[] } | undefined;
  const userIds = rawIds?.user_ids ?? [];
  if (!userIds.length) return [];

  const formatted = content?.formatted_body;
  if (typeof formatted === 'string') {
    const fromHtml = parseMentionsFromFormattedBody(formatted);
    if (fromHtml.length === userIds.length) {
      return fromHtml;
    }
  }

  return mentionDataFromBodyAndUserIds(body, userIds, room);
}

/**
 * Resolves the body text and mention data for the edit composer, substituting
 * stored (send-time) display names with current live names from the room.
 * Both the body text and the returned MentionData use the live display name so
 * that populateDivWithPlainTextAndMentions can locate and render chips correctly.
 */
export function buildLiveEditContent(
  body: string,
  content: Record<string, unknown> | undefined,
  room: Room | null
): { body: string; mentions: MentionData[] } {
  const storedMentions = resolveMentionDataForEdit(body, content, room);
  if (!storedMentions.length || !room) return { body, mentions: storedMentions };

  let updatedBody = body;
  let searchFrom = 0;

  const liveMentions: MentionData[] = storedMentions.map((m) => {
    const member = room.getMember(m.id);
    const liveDisplay = getMatrixDisplayName(member?.rawDisplayName, m.id);

    if (liveDisplay !== m.display) {
      const oldNeedle = `@${m.display}`;
      const idx = updatedBody.indexOf(oldNeedle, searchFrom);
      if (idx !== -1) {
        const newNeedle = `@${liveDisplay}`;
        updatedBody = updatedBody.slice(0, idx) + newNeedle + updatedBody.slice(idx + oldNeedle.length);
        searchFrom = idx + newNeedle.length;
      }
    } else {
      const needle = `@${m.display}`;
      const idx = updatedBody.indexOf(needle, searchFrom);
      if (idx !== -1) searchFrom = idx + needle.length;
    }

    return { id: m.id, display: liveDisplay };
  });

  return { body: updatedBody, mentions: liveMentions };
}
